import uuid from 'uuid/v4'
import { questionType, questionTitle } from '@edulastic/constants'

const LIST_POSITIONS = new Set(['top', 'bottom', 'left', 'right'])

const mapListPosition = (responsecontainerposition) =>
  LIST_POSITIONS.has(responsecontainerposition)
    ? responsecontainerposition
    : 'left'

const stripResponseTags = (stimulus = '') => {
  if (!stimulus) return ''
  if (window?.$) {
    const $el = window.$('<div>').append(stimulus)
    $el.find('response').each(function replaceResponse() {
      window.$(this).replaceWith(' ')
    })
    return $el.html()
  }
  return String(stimulus).replace(/<\/?response\b[^>]*>/gi, ' ')
}

const getDropAreaCount = (dragDropQuestion = {}) => {
  const { responseIds, stimulus, validation } = dragDropQuestion
  if (Array.isArray(responseIds) && responseIds.length) {
    return responseIds.length
  }
  if (window?.$ && stimulus) {
    return window.$(window.$('<div />').html(stimulus)).find('response').length
  }
  const validValue = validation?.validResponse?.value
  if (Array.isArray(validValue)) {
    return validValue.length
  }
  return 0
}

const toResponseId = (slot) => {
  if (slot == null || slot === '') return null
  if (typeof slot === 'object') {
    return slot.data ?? null
  }
  return slot
}

const normalizeChoice = (option) => {
  if (!option || typeof option !== 'object') return null

  // ClozeDragDrop shape: { value: id, label: html }
  if (option.value != null && (option.label != null || option.id == null)) {
    return {
      id: option.value,
      value: option.label ?? '',
    }
  }

  // Classification-like / imported shape: { id, value }
  if (option.id != null) {
    return {
      id: option.id,
      value: option.value ?? option.label ?? '',
    }
  }

  return null
}

const getChoices = (dragDropQuestion = {}) => {
  if (dragDropQuestion.hasGroupResponses) {
    const fromGroups = (
      dragDropQuestion.groupResponses || []
    ).flatMap((group) =>
      (group?.options || []).map(normalizeChoice).filter(Boolean)
    )
    if (fromGroups.length) {
      return fromGroups
    }
  }

  return (dragDropQuestion.options || []).map(normalizeChoice).filter(Boolean)
}

const getPossibleResponseGroups = (
  dragDropQuestion = {},
  possibleResponses
) => {
  if (dragDropQuestion.hasGroupResponses) {
    const groups = (dragDropQuestion.groupResponses || []).map((group) => ({
      title: group?.title || '',
      responses: (group?.options || []).map(normalizeChoice).filter(Boolean),
    }))
    if (groups.some((group) => group.responses.length)) {
      return groups
    }
  }
  return [
    {
      title: 'Group 1',
      responses: possibleResponses.map((response) => ({ ...response })),
    },
  ]
}

const mapSlotAnswersToCells = (slotValues = [], classifications = []) => {
  const slots = slotValues ?? []
  const answerMap = {}
  classifications.forEach((cell) => {
    answerMap[cell.id] = []
  })
  classifications.forEach((cell, index) => {
    const responseId = toResponseId(slots[index])
    if (responseId != null) {
      answerMap[cell.id] = [responseId]
    }
  })
  return answerMap
}

/**
 * Convert Cloze Drag & Drop → Classification.
 * Each drop area becomes one column in a single-row (1 × n) grid.
 */
export const convertClozeDragDropToClassification = (dragDropQuestion = {}) => {
  const {
    stimulus = '',
    uiStyle = {},
    duplicatedResponses = false,
    shuffleOptions = false,
    showDraghandle = false,
    hasGroupResponses = false,
    validation = {},
  } = dragDropQuestion

  const dropAreaCount = getDropAreaCount(dragDropQuestion)
  const columnCount = Math.max(1, dropAreaCount)

  const classifications = Array.from(
    { length: columnCount },
    (_, columnIndex) => ({
      id: uuid(),
      rowIndex: 0,
      columnIndex,
    })
  )

  const possibleResponses = getChoices(dragDropQuestion).filter(
    (choice) => choice?.id != null
  )
  const possibleResponseGroups = getPossibleResponseGroups(
    dragDropQuestion,
    possibleResponses
  )

  const validResponseValue = mapSlotAnswersToCells(
    validation?.validResponse?.value,
    classifications
  )
  const altResponses = (validation?.altResponses || []).map((alt) => ({
    score: alt?.score ?? 1,
    value: mapSlotAnswersToCells(alt?.value, classifications),
  }))

  const {
    responsecontainerposition,
    fontsize = 'normal',
    stemNumeration,
    choiceMinWidth,
    choiceMaxWidth,
  } = uiStyle

  return {
    title: questionTitle.CLASSIFICATION,
    type: questionType.CLASSIFICATION,
    firstMount: false,
    stimulus: stripResponseTags(stimulus),
    groupPossibleResponses: !!hasGroupResponses,
    possibleResponses,
    possibleResponseGroups,
    duplicateResponses: !!duplicatedResponses,
    shuffleOptions: !!shuffleOptions,
    classifications,
    uiStyle: {
      rowCount: 1,
      columnCount,
      rowTitles: [''],
      columnTitles: Array.from(
        { length: columnCount },
        (_, index) => `COLUMN ${index + 1}`
      ),
      rowMinHeight: '100px',
      possibilityListPosition: mapListPosition(responsecontainerposition),
      fontsize,
      showDragHandle: !!showDraghandle,
      ...(choiceMinWidth != null ? { choiceMinWidth } : {}),
      ...(choiceMaxWidth != null ? { choiceMaxWidth } : {}),
      ...(stemNumeration ? { validationStemNumeration: stemNumeration } : {}),
    },
    validation: {
      scoringType: validation?.scoringType || 'exactMatch',
      validResponse: {
        score: validation?.validResponse?.score ?? 1,
        value: validResponseValue,
      },
      altResponses,
    },
  }
}
